import find from 'lodash/find';
import get from 'lodash/get';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { isEditable } from 'helpers/menus';
import * as fromSendABox from 'reducers/sendABox';

import basketEdits, * as fromBasketEdits from './basketEdits';
import baskets, * as fromBaskets from './baskets';
import basketStatus, * as fromBasketStatus from './basketStatus';
import basketSummaryStatus, * as fromBasketSummaryStatus from './basketSummaryStatus';
import customizeIt, * as fromCustomizeIt from './customizeIt';
import mealPrices, * as fromMealPrices from './mealPrices';
import mealReviewStats from './mealReviewStats';
import mealSellouts from './mealSellouts';
import menuCategories, * as fromMenuCategories from './menuCategories';
import menuMeals, * as fromMenuMeals from './menuMeals';
import menus, * as fromMenu from './menus';
import menuSlugs from './menuSlugs';
import menuStatus, * as fromMenuStatus from './menuStatus';
import modalMeals, * as fromModalMeals from './modalMeals';
import pastReceipts, * as fromPastReceipts from './pastReceipts';
import quickFilters from './quickFilters';
import reviews, * as fromReviews from './reviews';
import scroll, * as fromScroll from './scroll';
import status, * as fromStatus from './status';
import streakOffers from './streakOffers';
import suggestedPairings, * as fromSuggestedPairings from './suggestedPairings';
import userBehavioralDiscounts, * as fromUserBehavioralDiscounts from './userBehavioralDiscounts';

export default combineReducers({
  baskets,
  basketEdits,
  basketStatus,
  basketSummaryStatus,
  customizeIt,
  mealPrices,
  mealReviewStats,
  mealSellouts,
  menuCategories,
  menuMeals,
  menus,
  menuSlugs,
  menuStatus,
  modalMeals,
  pastReceipts,
  quickFilters,
  reviews,
  scroll,
  status,
  suggestedPairings,
  streakOffers,
  userBehavioralDiscounts,
});

const selectDashboard = state => state.dashboard;

const selectBasketStatus = state => selectDashboard(state).basketStatus;
const selectBasketSummaryStatus = state => selectDashboard(state).basketSummaryStatus;
const selectMenuStatus = state => selectDashboard(state).menuStatus;
const selectMealPricesState = state => selectDashboard(state).mealPrices;
const selectMealReviewStatsState = state => selectDashboard(state).mealReviewStats;

export const selectQuickFilters = state => selectDashboard(state).quickFilters;
export const selectBaskets = state => selectDashboard(state).baskets;
export const selectBasketEdits = state => selectDashboard(state).basketEdits;
export const selectCustomizeIt = state => selectDashboard(state).customizeIt;
export const selectMealOptions = state => selectDashboard(state).mealOptions;
export const selectMenus = state => selectDashboard(state).menus;
export const selectMealOptionGroups = state => selectDashboard(state).mealOptionGroups;
export const selectMenuSlugs = state => selectDashboard(state).menuSlugs;
export const selectMenuCategories = state => selectDashboard(state).menuCategories;
export const selectMenuMeals = state => selectDashboard(state).menuMeals;
export const selectModalMeals = state => selectDashboard(state).modalMeals;
export const selectScroll = state => selectDashboard(state).scroll;
export const selectStatus = state => selectDashboard(state).status;
export const selectPastReceipts = state => selectDashboard(state).pastReceipts;
export const selectMealSellouts = state => selectDashboard(state).mealSellouts;

export const selectMeal = (state, menuSlug, mealId) => {
  return (
    find(selectMealsForBasket(state, menuSlug), { id: mealId }) ||
    find(selectMealsForMenu(state, menuSlug), { id: mealId })
  );
};

export const selectMealForMenu = (state, menuSlug, mealId) =>
  find(selectMealsForMenu(state, menuSlug), { id: mealId });

export const selectAvailableSuggestedPairings = state => selectDashboard(state).suggestedPairings;

export const selectAvailableStreakOffers = state => selectDashboard(state).streakOffers;

export const selectAvailableUserBehavioralDiscounts = state =>
  selectDashboard(state).userBehavioralDiscounts;

export const selectReviews = state => selectDashboard(state).reviews;

export const selectBasket = (state, menuSlug) =>
  fromBaskets.selectBasket(selectBaskets(state), menuSlug);

export const selectLastOrderableMenuSlug = state =>
  fromBaskets.selectLastOrderableMenuSlug(selectBaskets(state));

export const selectPastReceipt = (state, menuSlug) => {
  return fromPastReceipts.selectReceipt(selectPastReceipts(state), menuSlug);
};

export const selectPastReceiptLoading = state =>
  fromPastReceipts.selectReceiptLoading(selectPastReceipts(state));

export const selectMenu = (state, menuSlug) => fromMenu.selectMenu(selectMenus(state), menuSlug);

export const selectMealAddons = createSelector(selectMenuMeals, menuMeals =>
  fromMenuMeals.selectMealAddons(menuMeals)
);

export const selectOptIns = createSelector(selectMenuMeals, menuMeals =>
  fromMenuMeals.selectOptIns(menuMeals)
);

export const selectMealsForMenu = (state, menuSlug) =>
  fromMenuMeals.selectMealsForMenu(selectMenuMeals(state), menuSlug);

export const selectNonDonationMealsForMenu = (state, menuSlug) =>
  fromMenuMeals.selectNonDonationMealsForMenu(selectMenuMeals(state), menuSlug);

export const selectMealsForBasket = (state, menuSlug) =>
  fromBaskets.selectMealsForBasket(selectBaskets(state), menuSlug);

export const selectModalMealsForMenu = (state, menuSlug) =>
  fromModalMeals.selectModalMealsForMenu(selectModalMeals(state), menuSlug);

export const selectModalMealsCategoryForMenu = (state, menuSlug) =>
  fromModalMeals.selectModalMealsCategory(selectModalMeals(state), menuSlug);

export const selectSuggestedPairingsForMenu = (state, menuSlug) =>
  fromSuggestedPairings.selectSuggestedPairingsForMenu(
    selectAvailableSuggestedPairings(state),
    menuSlug
  );

export const selectSelectedSuggestedPairingsForMenu = (state, menuSlug) =>
  fromSuggestedPairings.selectSelectedSuggestedPairingsForMenu(
    selectAvailableSuggestedPairings(state),
    menuSlug
  );

export const selectSelectedSuggestedPairingForMenuAndHostMeal = (state, menuSlug, hostMealId) =>
  fromSuggestedPairings.selectSelectedSuggestedPairingForMenuAndHostMeal(
    selectAvailableSuggestedPairings(state),
    menuSlug,
    hostMealId
  );

export const basketNeedsInitialization = (state, menuSlug) =>
  fromBasketStatus.needsInitialization(selectBasketStatus(state), menuSlug);

export const isBasketLoading = (state, menuSlug) =>
  fromBasketStatus.isBasketLoading(selectBasketStatus(state), menuSlug);

export const isBasketError = (state, menuSlug) =>
  fromBasketStatus.isBasketError(selectBasketStatus(state), menuSlug);

export const removedMeals = (state, menuSlug) =>
  fromBasketStatus.removedMeals(selectBasketStatus(state), menuSlug);

export const isBasketSummaryLoaded = (state, menuSlug) =>
  fromBasketSummaryStatus.isLoaded(selectBasketSummaryStatus(state), menuSlug);

export const isBasketSummaryLoading = (state, menuSlug) =>
  fromBasketSummaryStatus.isLoading(selectBasketSummaryStatus(state), menuSlug);

export const isBasketSummaryError = (state, menuSlug) =>
  fromBasketSummaryStatus.isError(selectBasketSummaryStatus(state), menuSlug);

export const isInitializationComplete = state =>
  fromStatus.isInitializationComplete(selectStatus(state));

export const needsInitializing = state => fromStatus.needsInitializing(selectStatus(state));

export const isUnauthorized = state => fromStatus.isUnauthorized(selectStatus(state));

export const menuNeedsInitialization = (state, menuSlug) =>
  fromMenuStatus.needsInitialization(selectMenuStatus(state), menuSlug);

export const isMenuLoading = (state, menuSlug) =>
  fromMenuStatus.isMenuLoading(selectMenuStatus(state), menuSlug);

export const isMenuError = (state, menuSlug) =>
  fromMenuStatus.isMenuError(selectMenuStatus(state), menuSlug);

export const isMenuRefreshLoading = (state, menuSlug) =>
  fromMenuStatus.isMenuRefreshLoading(selectMenuStatus(state), menuSlug);

export const selectScrollPositionForRoute = (state, route) =>
  fromScroll.selectScrollPositionForRoute(selectScroll(state), route);

export const selectEditableMenuSlugs = createSelector(
  selectMenuSlugs,
  selectBaskets,
  (menuSlugs, baskets) => menuSlugs.filter(slug => get(baskets, [slug, 'futureBasket'], false))
);

export const selectInitialMenuSlugs = createSelector(selectMenuSlugs, menuSlugs =>
  menuSlugs.slice(0, 2)
);

export const makeSelectRelativeMenuWeek = () =>
  createSelector(
    [selectMenuSlugs, selectBaskets, (_state, menuSlug) => menuSlug],
    (slugs, basketsBySlug, menuSlug) => {
      const editableSlugs = (slugs || []).filter(slug => isEditable(basketsBySlug?.[slug]));
      const idx = editableSlugs.indexOf(menuSlug);

      return idx >= 0 ? idx : null;
    }
  );

const sortAsc = (x, y) => x - y;

export const selectOrderedBaskets = createSelector(selectBaskets, baskets => {
  return Object.values(baskets).sort((basketA, basketB) => {
    return sortAsc(new Date(basketA.menuEndsAt), new Date(basketB.menuEndsAt));
  });
});

export const selectNextOrderableBasket = createSelector(selectOrderedBaskets, orderedBaskets => {
  return find(orderedBaskets, {
    scheduled: true,
    futureBasket: true,
  });
});

export const selectNextOrderableSkippedBasket = createSelector(
  selectOrderedBaskets,
  orderedBaskets => {
    return find(orderedBaskets, {
      futureBasket: true,
      skipped: true,
    });
  }
);

export const selectCustomizeItForMealId = (state, mealId) =>
  fromCustomizeIt.selectCustomizeItForMealId(selectCustomizeIt(state), mealId);

export const selectMealServingsForBasketEdits = state =>
  fromBasketEdits.selectServings(selectBasketEdits(state));

export const selectMealOptionsForBasketEdits = state =>
  fromBasketEdits.selectMealOptions(selectBasketEdits(state));

export const selectMealOptionsForMeal = (state, mealId) =>
  fromBasketEdits.selectMealOptionsForMeal(selectBasketEdits(state), mealId);

export const selectMealOptionsForBasketAndMeal = (state, mealId) =>
  fromBasketEdits.selectMealOptions(state, mealId);

export const selectSubtotalsForBasketEdits = state =>
  fromBasketEdits.selectSubtotals(selectBasketEdits(state));

export const selectNutritionSummariesForBasketEdits = state =>
  fromBasketEdits.selectNutritionSummaries(selectBasketEdits(state));

export const selectPairingsForBasketEdits = state =>
  fromBasketEdits.selectSuggestedPairings(selectBasketEdits(state));

export const selectBasketEditsIsLoaded = state =>
  fromBasketEdits.selectBasketIsLoaded(selectBasketEdits(state));

export const selectBasketDirty = state =>
  fromBasketEdits.selectBasketDirty(selectBasketEdits(state));

export const selectReview = (state, mealId) =>
  fromReviews.selectReview(selectReviews(state), mealId);

export const selectComments = state => fromReviews.selectComments(selectReviews(state));

export const selectReviewReasonList = state =>
  fromReviews.selectReviewReasons(selectReviews(state)).reasons;

export const selectMenuCategoriesLoading = (state, slug) =>
  fromMenuCategories.selectMenuCategoriesLoading(selectMenuCategories(state), slug);

export const selectMenuCategoriesData = (state, slug) =>
  fromMenuCategories.selectMenuCategoriesData(selectMenuCategories(state), slug);

export const selectMealReviewStats = (state, mealId, menuSlug) =>
  selectMealReviewStatsState(state)[menuSlug]?.[mealId] || {};

export const selectMealPriceForMealDetails = (state, menuSlug, mealId, isSendABox) => {
  if (mealId == null || menuSlug === null) return null;

  if (isSendABox) {
    return fromSendABox.sendABoxMealPrice(state, mealId);
  }

  return fromMealPrices.selectMealPrice(selectMealPricesState(state), menuSlug, mealId);
};

export const selectMealPriceError = state =>
  fromMealPrices.selectMealPriceError(selectMealPricesState(state));

export const selectUserBehavioralDiscountsForMenu = (state, menuSlug) =>
  fromUserBehavioralDiscounts.selectUserBehavioralDiscountsForMenu(
    selectAvailableUserBehavioralDiscounts(state),
    menuSlug
  );

export const selectUserBehavioralDiscountsForPromotionalDisplay = (state, menuSlug) =>
  fromUserBehavioralDiscounts.selectUserBehavioralDiscountsForPromotionalDisplay(
    selectAvailableUserBehavioralDiscounts(state),
    menuSlug
  );

export const selectUserBehavioralDiscountsIsLoading = state =>
  fromUserBehavioralDiscounts.selectUserBehavioralDiscountsIsLoading(
    selectAvailableUserBehavioralDiscounts(state)
  );

export const selectClaimedUserBehavioralDiscountIdForMenu = (state, menuSlug) =>
  fromUserBehavioralDiscounts.selectClaimedUserBehavioralDiscountIdForMenu(
    selectAvailableUserBehavioralDiscounts(state),
    menuSlug
  );

export const partitionUserBehavioralDiscountsForPromotionalDisplay = (state, menuSlug) =>
  fromUserBehavioralDiscounts.partitionUserBehavioralDiscountsForPromotionalDisplay(
    selectAvailableUserBehavioralDiscounts(state),
    menuSlug
  );

export const selectUserBehavioralDiscountConfirmationDetails = state =>
  fromUserBehavioralDiscounts.selectConfirmationDetails(
    selectAvailableUserBehavioralDiscounts(state)
  );

export const selectDeferredClaimAnimationId = state =>
  fromUserBehavioralDiscounts.selectDeferredClaimAnimationId(
    selectAvailableUserBehavioralDiscounts(state)
  );
